import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Timeline } from '../components/about/Timeline';
import { ServicesAccordion } from '../components/about/ServicesAccordion';
import { ProjectRequestWizard } from '../components/about/ProjectRequestWizard';
import { Sparkles, Target, Lightbulb } from 'lucide-react';
export function About() {
  return <>
      <Helmet data-id="element-1277">
        <title data-id="element-1278">About Us | Bright Pixxel Studio</title>
        <meta name="description" content="A UK family web design studio building affordable, high-quality websites for small businesses since 2018. Meet the team and see how we work." data-id="element-1279" />
        <link rel="canonical" href="https://bright-pixxel.com/about" data-id="element-1280" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-1281" />
        <meta property="og:type" content="website" data-id="element-1282" />
        <meta property="og:title" content="About Us | Bright Pixxel Studio" data-id="element-1283" />
        <meta property="og:description" content="A UK family web design studio building affordable, high-quality websites for small businesses since 2018. Meet the team and see how we work." data-id="element-1284" />
        <meta property="og:url" content="https://bright-pixxel.com/about" data-id="element-1285" />
        <meta property="og:site_name" content="Bright Pixxel Studio" data-id="element-1286" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1287" />
        <meta property="og:image:width" content="1200" data-id="element-1288" />
        <meta property="og:image:height" content="630" data-id="element-1289" />
        <meta property="og:image:type" content="image/png" data-id="element-1290" />
        <meta property="og:image:alt" content="Bright Pixxel Studio - UK Family Web Design Business" data-id="element-1291" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-1292" />
        <meta name="twitter:title" content="About Us | Bright Pixxel Studio" data-id="element-1293" />
        <meta name="twitter:description" content="A UK family web design studio building affordable, high-quality websites for small businesses since 2018. Meet the team and see how we work." data-id="element-1294" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-1295" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studio - UK Family Web Design Business" data-id="element-1296" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1297">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1298">
          <div className="flex flex-col md:flex-row items-center md:space-x-12" data-id="element-1299">
            <div className="md:w-1/2 mb-8 md:mb-0" data-id="element-1300">
              <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1301">
                About Us
              </motion.span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1302">
                UK Web Design Agency Delivering Websites That Convert
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-xl text-gray-700 dark:text-gray-200 mb-6 font-medium" data-id="element-1303">
                Since 2018 we've built over 500 websites for UK businesses of
                all sizes.
              </motion.p>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-lg text-gray-600 dark:text-gray-300 mb-6" data-id="element-1304">
                Bright Pixxel Studio is a UK-based family business with an
                SEO-first approach that focuses on delivering measurable
                business results. We've helped businesses across various
                industries establish their online presence and grow their
                customer base.
              </motion.p>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1305">
                Our team combines technical expertise with creative design to
                create websites that not only look beautiful but also perform
                exceptionally well in search engines and convert visitors into
                customers.
              </motion.p>
            </div>
            <div className="md:w-1/2" data-id="element-1306">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="relative rounded-xl overflow-hidden shadow-xl" data-id="element-1307">
                <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Bright Pixxel team collaboration" className="w-full h-auto" data-id="element-1308" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-1309">
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-1310">
                    <div className="flex items-center" data-id="element-1311">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2" data-id="element-1312"></div>
                      <p className="text-white text-sm font-medium" data-id="element-1313">
                        UK-based team dedicated to your success
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1314">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1315">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1316">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1317">
              Our Journey
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1318">
              The Bright Pixxel Story
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1319">
              From our humble beginnings to becoming a trusted web design
              partner for businesses across the UK, our journey has been defined
              by continuous growth and innovation.
            </motion.p>
          </div>
          <Timeline data-id="element-1320" />
        </div>
      </section>
      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1321">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1322">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1323">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1324">
              Our Values
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1325">
              What Drives Us
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1326">
              Our core values shape everything we do, from how we design
              websites to how we interact with our clients.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1327">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1328">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1329">
                <Sparkles size={24} className="text-purple-600" data-id="element-1330" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1331">Quality</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1332">
                We never compromise on quality. Every website we build is
                crafted with attention to detail, using best practices for
                performance, security, and user experience.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1333">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1334">
                <Target size={24} className="text-blue-600" data-id="element-1335" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1336">Transparency</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1337">
                We believe in clear communication and transparent pricing.
                You'll always know what you're paying for and what to expect
                throughout the project lifecycle.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1338">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1339">
                <Lightbulb size={24} className="text-green-600" data-id="element-1340" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1341">Innovation</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1342">
                We stay at the forefront of web design trends and technologies,
                continuously learning and evolving to provide innovative
                solutions for our clients' challenges.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Services Accordion Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1343">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1344">
          <div className="max-w-3xl mx-auto" data-id="element-1345">
            <div className="text-center mb-16" data-id="element-1346">
              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1347">
                Our Services
              </motion.span>
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1348">
                What We Offer
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1349">
                From standard websites to e-commerce solutions and ongoing
                maintenance, we provide a comprehensive range of digital
                services.
              </motion.p>
            </div>
            <ServicesAccordion data-id="element-1350" />
          </div>
        </div>
      </section>
      {/* Project Request Wizard Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1351">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1352">
          <div className="max-w-4xl mx-auto" data-id="element-1353">
            <div className="text-center mb-16" data-id="element-1354">
              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1355">
                Start Your Project
              </motion.span>
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1356">
                Tell Us About Your Project
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1357">
                Use our project request wizard to help us understand your needs
                and provide you with a tailored solution.
              </motion.p>
            </div>
            <ProjectRequestWizard data-id="element-1358" />
          </div>
        </div>
      </section>
    </>;
}